import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import LoadingIndicator from './components/LoadingIndicator';
import './App.css';

const Navigation = lazy(() => import('./components/Navigation'));
const WelcomeSection = lazy(() => import('./components/WelcomeSection'));
const AboutSection = lazy(() => import('./components/AboutSection'));
const VideoGallerySection = lazy(() => import('./components/VideoGallerySection'));
const AssistWorkSection = lazy(() => import('./components/AssistWorkSection'));
const ContactSection = lazy(() => import('./components/ContactSection'));
const NotFoundPage = lazy(() => import('./components/NotFoundPage'));
const Footer = lazy(() => import('./components/Footer'));
const ErrorBoundary = lazy(() => import('./components/ErrorBoundary'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./components/TermsOfService'));
const ScrollToTop = lazy(() => import('./components/ScrollToTop'));

function AppContent() {
  const [content, setContent] = useState(null);
  const [appLoading, setAppLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    fetch('/content.json')
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(data => {
        setContent(data);
        setAppLoading(false);
      })
      .catch(error => {
        console.error('There has been a problem with your fetch operation:', error);
        setAppLoading(false);
      });
  }, []);

  // Handle hash-based navigation - only on main page
  useEffect(() => {
    if (location.pathname !== '/') return;

    const handleHashChange = () => {
      const hash = window.location.hash.substring(1); // Remove the # symbol
      if (hash) {
        const element = document.getElementById(hash);
        if (element) {
          // Calculate offset based on section (same as Navigation component)
          let offset = 80; // Default offset for navigation bar
          
          if (hash === 'about' || hash === 'video-gallery' || hash === 'contact') {
            offset = 80; // Higher offset for these sections
          }
          
          const elementPosition = element.offsetTop - offset;
          window.scrollTo({
            top: elementPosition,
            behavior: 'smooth'
          });
        }
      }
    };

    // Handle initial hash on page load
    if (window.location.hash) {
      handleHashChange();
    }

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [location.pathname]);

  // Update URL hash when scrolling to sections - only on main page
  useEffect(() => {
    if (location.pathname !== '/') return;

    const sections = ['welcome', 'about', 'assist-work', 'video-gallery', 'contact'];
    
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100; // Offset for navigation

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            const currentHash = window.location.hash.substring(1);
            if (currentHash !== sectionId) {
              window.history.replaceState(null, null, `#${sectionId}`);
            }
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname !== '/') return;

    const handleKeyDown = (event) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        
        const sections = ['welcome', 'about', 'assist-work', 'video-gallery', 'contact'];
        const currentSection = sections.find(section => {
          const element = document.getElementById(section);
          if (element) {
            const rect = element.getBoundingClientRect();
            return rect.top <= 100 && rect.bottom >= 100;
          }
          return false;
        });

        if (currentSection) {
          const currentIndex = sections.indexOf(currentSection);
          let nextIndex;

          if (event.key === 'ArrowDown') {
            nextIndex = Math.min(currentIndex + 1, sections.length - 1);
          } else {
            nextIndex = Math.max(currentIndex - 1, 0);
          }

          const nextSection = document.getElementById(sections[nextIndex]);
          if (nextSection) {
            nextSection.scrollIntoView({ behavior: 'smooth' });
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [location.pathname]);

  if (appLoading) {
    return <LoadingIndicator />;
  }

  return (
    <Routes>
      <Route path="/" element={
        <>
          <Navigation content={content.navigation} />
          <WelcomeSection content={content.welcome} />
          <AboutSection content={content.about} />
          <AssistWorkSection content={content.assistWork} />
          <VideoGallerySection content={content.videoGallery} />
          <ContactSection content={content.contact} />
          <Footer content={content.site} />
        </>
      } />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <Suspense fallback={<LoadingIndicator />}>
        <ErrorBoundary>
          <div className="App">
            <ScrollToTop />
            <AppContent />
          </div>
        </ErrorBoundary>
      </Suspense>
    </Router>
  );
}

export default App;